import { Component, effect, inject, input, OnDestroy, signal } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { dsDtos, userDtos, webDtos } from '@vectronic/openApi-customer';
import { EXTERNAL_MAP_LAYERS_SERVICE } from '../../services/external-layers/external-map-layers.service';
import { MapService } from '../../services/map/map.service';
import { VirtualFenceService } from '../../services/virtualFence/virtual-fence.service';
@Component({
  selector: 'bm-base-map',
  imports: [LeafletModule, MatProgressSpinnerModule],
  templateUrl: './base-map.component.html',
  styleUrl: './base-map.component.scss',
})
export class BaseMapComponent implements OnDestroy {
  mapService = inject(MapService);
  private virtualFenceService = inject(VirtualFenceService);
  private externalLayersService = inject(EXTERNAL_MAP_LAYERS_SERVICE, { optional: true });

  mapPositions = input<webDtos.MapPositionsList[]>([]);
  speciesMap = input<Map<number, dsDtos.AnimalSpecies>>();
  loading = input(false);
  userPreferences = input<userDtos.UserPreferences>();
  isVirtualFenceEnabled = input(false);
  isViewOnlyMode = input(false);
  isAnimationPlayActive = input(false);
  hidePrecisionButtons = input(false);
  shouldResetMapZoom = input(true);
  popupCustomizer = input<(position: webDtos.GPSPosition, defaultHtml: string) => string>();

  private isMapReady = signal(false);
  isRendering = signal(false);
  private wasAnimationPlayActive = false;

  displayPositionsEffect = effect(() => {
    if (!this.isMapReady()) return;
    if (this.loading()) return;

    if (this.isAnimationPlayActive()) {
      if (!this.wasAnimationPlayActive) {
        this.mapService.softReset(true);
      }
      this.mapService.setMapPositions(this.mapPositions());
      this.wasAnimationPlayActive = true;
      return;
    }
    this.wasAnimationPlayActive = false;
    this.isRendering.set(true);
    this.mapService.displayPositions(
      this.mapPositions(),
      this.speciesMap(),
      this.shouldResetMapZoom(),
    );
    this.mapService.focusRequestedItem(this.mapPositions());
    this.isRendering.set(false);
  });

  checkMapQueueEffect = effect(() => {
    if (this.userPreferences() && this.mapWaitingForPreferences)
      this.onMapReady(this.mapWaitingForPreferences);
  });

  private popupCustomizerEffect = effect(() => {
    this.mapService.mapMarkerService.popupCustomizer = this.popupCustomizer();
  });

  private mapWaitingForPreferences: L.Map;
  private attachedMap?: L.Map;
  onMapReady(leafletMap: L.Map) {
    if (!this.userPreferences()) {
      this.mapWaitingForPreferences = leafletMap;
      return;
    }
    this.mapWaitingForPreferences = undefined;

    this.attachedMap = leafletMap;
    leafletMap.invalidateSize();
    this.mapService.set(leafletMap, this.userPreferences());
    if (this.isVirtualFenceEnabled()) this.virtualFenceService.setMap(leafletMap);
    this.externalLayersService?.setMap(leafletMap);
    this.isMapReady.set(true);
  }

  ngOnDestroy() {
    this.mapService.mapMarkerService.popupCustomizer = undefined;
    // A base-map instance that never attached (its preferences never arrived)
    // owns no shared state; resetting the services here would tear down the
    // state of a map that is still alive underneath.
    if (this.attachedMap) {
      this.mapService.reset(this.attachedMap);
      this.externalLayersService?.reset(this.attachedMap);
    }
    this.virtualFenceService.cancelNewPolygonCreation();
  }
}
